import PropTypes from 'prop-types';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import dynamic from 'next/dynamic';

// Components
import ResidenceCard from '../../atoms/ResidenceCard/ResidenceCard';
import Button from '../../atoms/Button/Button';
import Tag from '../../atoms/Tag/Tag';
import RichTextContent from '../../atoms/RichTextContent/RichTextContent';
import OpeningDateTag from '../../molecules/OpeningDateTag/OpeningDateTag';

// Style
import styles from './Search.module.css';

// Hooks
import useResponsiveQueries from '../../../src/hooks/useResponsiveQueries';

// Utils
import { getSlugResidence } from '../../../src/utils/pageSlug';

const GoogleMapLocations = dynamic(() => import('../../atoms/GoogleMapLocations/GoogleMapLocations'), {
  ssr: false,
});

function Search({ children, list, pageData }) {
  // States
  const listLocations = list.map(({
    pmsId,
    latitude,
    longitude,
    media,
    address,
    nameShort,
    openingDate,
    full,
    minPrice,
    slug,
  }) => ({
    lat: +latitude,
    lng: +longitude,
    pmsId,
    nameShort,
    address,
    media,
    openingDate,
    full,
    minPrice,
    slug,
  }));

  // Hooks
  const { t } = useTranslation();
  const router = useRouter();
  const { isMobile } = useResponsiveQueries();

  // Handlers
  const handleMarkerClick = (_event, location) => {
    const residenceCardElement = document.querySelector(`#pms${location.pmsId}`);
    residenceCardElement.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
    });
  };

  const handleHelpButtonClick = () => {
    router.push(pageData?.helpButtonURL);
  };

  return (
    <>
      <div className={styles.content}>
        <div className={styles.results}>
          <h1>{pageData?.title}</h1>
          <RichTextContent
            content={pageData?.subtext}
            className={styles.intro}
          />

          <div className={styles.list}>
            {listLocations?.length
              ? listLocations.map((listItem) => (
                <ResidenceCard
                  classNameLink={styles.card}
                  id={`pms${listItem?.pmsId}`}
                  name={listItem?.nameShort}
                  key={listItem?.pmsId}
                  city={listItem?.city}
                  address={listItem?.address}
                  price={listItem?.minPrice > 0 ? t('residencesBlock.price', { price: listItem?.minPrice }) : ''}
                  media={listItem?.media}
                  slug={getSlugResidence(router.locale, listItem?.slug)}
                  template="search"
                  TitleTag="h2"
                  tag={(
                    <>
                      <OpeningDateTag openingDate={listItem?.openingDate} />
                      {listItem?.full ? <Tag content={t('search.isFullLabel')} color="red" /> : null}
                    </>
                    )}
                />
              ))
              : <p className={styles.empty}>{t('search.emptyList')}</p>}
            <div className={styles.help} style={{ gridArea: '3/1/3/3' }}>
              <p className={styles.helpTitle}>{pageData?.helpTitle}</p>
              <RichTextContent
                content={pageData?.helpText}
                className={styles.helpText}
              />

              {pageData?.helpButtonLabel && pageData?.helpButtonURL
                ? (
                  <Button onClick={handleHelpButtonClick} outline>
                    {pageData?.helpButtonLabel}
                  </Button>
                ) : null}
            </div>
          </div>
        </div>
        {!isMobile ? (
          <aside className={styles.aside}>
            <GoogleMapLocations
              id="search-google-maps"
              locations={listLocations}
              onMarkerClick={handleMarkerClick}
            />
          </aside>
        ) : null}
      </div>
      {children}
    </>
  );
}

Search.propTypes = {
  children: PropTypes.node,
  list: PropTypes.arrayOf(PropTypes.shape({
    pmsId: PropTypes.string,
    nameShort: PropTypes.string,
    address: PropTypes.string,
    minPrice: PropTypes.number,
    latitude: PropTypes.string,
    longitude: PropTypes.string,
  })),
  pageData: PropTypes.shape({
    cities: PropTypes.arrayOf(PropTypes.shape({})),
    regions: PropTypes.arrayOf(PropTypes.shape({})),
    searchTitle: PropTypes.string,
    title: PropTypes.string,
    subtext: PropTypes.string,
    helpTitle: PropTypes.string,
    helpText: PropTypes.string,
    helpButtonLabel: PropTypes.string,
    helpButtonURL: PropTypes.string,
    toggleLabel: PropTypes.string,
    emptyList: PropTypes.string,
  }).isRequired,
};

Search.defaultProps = {
  children: null,
  list: [],
};

export default Search;
